import { ErrorBox, PageSection, Spinner } from "@client/components/ui";
import { isSortKey, isStatusFilter, type SortKey, type StatusFilter } from "@client/features/watchlist/lib";
import { TokenDetailPanel } from "@client/features/watchlist/TokenDetailPanel";
import { WatchTable } from "@client/features/watchlist/WatchTable";
import { basketsQuery, overviewQuery, settingsQuery, tokensQuery } from "@client/lib/queries";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

const WatchlistaSearchSchema = z.object({
  sel: z.string().optional(),
  status: z.string().optional(),
  sort: z.string().optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
export type WatchlistaSearch = z.infer<typeof WatchlistaSearchSchema>;

export const Route = createFileRoute("/watchlista")({
  validateSearch: (search): WatchlistaSearch => WatchlistaSearchSchema.parse(search),
  component: WatchlistaPage,
});

function WatchlistaPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const tokensQ = useQuery(tokensQuery());
  const basketsQ = useQuery(basketsQuery());
  const settingsQ = useQuery(settingsQuery());
  const overviewQ = useQuery(overviewQuery());

  const status: StatusFilter = search.status && isStatusFilter(search.status) ? search.status : "wszystkie";
  const sortKey: SortKey = search.sort && isSortKey(search.sort) ? search.sort : "mc";
  const dir = search.dir ?? "desc";
  const selectedId = search.sel ?? null;

  useEffect(() => {
    if (!selectedId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") navigate({ search: (prev) => ({ ...prev, sel: undefined }) });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedId, navigate]);

  const error = tokensQ.error ?? basketsQ.error ?? settingsQ.error ?? overviewQ.error;
  if (error) {
    return (
      <PageSection>
        <ErrorBox error={error} />
      </PageSection>
    );
  }

  const tokens = tokensQ.data;
  const baskets = basketsQ.data;
  const settings = settingsQ.data;
  if (!tokens || !baskets || !settings) {
    return (
      <PageSection>
        <div className="flex justify-center py-20">
          <Spinner size={28} />
        </div>
      </PageSection>
    );
  }

  const selectedToken = selectedId ? (tokens.find((t) => t.id === selectedId) ?? null) : null;
  const fx = overviewQ.data?.fx ?? null;

  return (
    <section className="flex flex-col items-stretch gap-4 p-3 md:flex-row md:items-start md:p-6">
      <WatchTable
        tokens={tokens}
        baskets={baskets}
        status={status}
        sortKey={sortKey}
        dir={dir}
        selectedId={selectedId}
        onStatusChange={(next) => navigate({ search: (prev) => ({ ...prev, status: next }) })}
        onSortChange={(key) =>
          navigate({
            search: (prev) => ({
              ...prev,
              sort: key,
              dir: sortKey === key ? (dir === "desc" ? "asc" : "desc") : "desc",
            }),
          })
        }
        onSelect={(tokenId) => navigate({ search: (prev) => ({ ...prev, sel: tokenId }) })}
        onAddTokenClick={() => document.querySelector<HTMLInputElement>("[data-search-input]")?.focus()}
      />
      {selectedToken && (
        <TokenDetailPanel
          key={selectedToken.id}
          token={selectedToken}
          fx={fx}
          plnBeside={settings.plnBeside}
          onClose={() => navigate({ search: (prev) => ({ ...prev, sel: undefined }) })}
        />
      )}
    </section>
  );
}
